// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Card component for displaying customer value metrics.
 * Shows lifetime value and active ARR, each split by currency.
 */
import { CardHeader } from '@/components/card-header';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { Currency } from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { getCustomerValue } from '@/customer/api/get-customer-value';
import { Customer } from '@/customer/api/types';
import { Box, Divider, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';

interface Props {
  customer: Customer;
}

function CurrencyValueList(props: { values: Currency[] }) {
  if (props.values.length === 0) {
    return <Typography variant="h3">{formatCurrencyValue(null)}</Typography>;
  }

  return (
    <Stack spacing={0.5} alignItems="center">
      {props.values.map((value) => (
        <Typography key={value.isoCode} variant="h3">
          {formatCurrencyValue(value)}
        </Typography>
      ))}
    </Stack>
  );
}

export function CustomerValueStatsCard(props: Props) {
  const { data } = useSuspenseQuery({
    queryKey: ['customer-value', props.customer.id],
    queryFn: () => getCustomerValue(props.customer.id),
  });

  return (
    <Paper variant="outlined">
      <CardHeader title="Customer value" />
      <Divider />
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        gap={2}
        margin={2}
      >
        <Stack
          padding={2}
          spacing={2}
          alignItems="center"
          justifyContent="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5">ARR</Typography>
            <Tooltip title="Annual recurring revenue" arrow>
              <InfoOutlined color="action" fontSize="small" />
            </Tooltip>
          </Stack>
          <CurrencyValueList values={data.activeARR} />
        </Stack>
        <Stack
          padding={2}
          spacing={2}
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="h5">Lifetime</Typography>
          <CurrencyValueList values={data.lifetimeValue} />
        </Stack>
      </Box>
    </Paper>
  );
}
